import UndoIcon from '@duyank/icons/regular/ArrowArcLeft';
import RedoIcon from '@duyank/icons/regular/ArrowArcRight';
import ImageIcon from '@duyank/icons/regular/FileImage';
import DocumentIcon from '@duyank/icons/regular/File';
import { File, useEditor } from '@lidojs/design-editor';
import { Menu, MenuItem, MenuButton } from '@szhsin/react-menu';
import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/slide.css';
import React, {
  ChangeEvent,
  forwardRef,
  ForwardRefRenderFunction,
  Fragment,
  useEffect,
  useRef,
  useState,
} from 'react';
import { downloadObjectAsJson } from '../utils/download';
import Popover from 'libs/design-editor/src/common/popover/Popover';
import SettingButton from 'libs/design-editor/src/settings/SettingButton';
import { SerializedPage } from '@lidojs/design-core/types/page';
import AppContext from 'libs/AppContext';
import axios from 'axios';

interface HeaderLayoutProps {
  openPreview: () => void;
  downloadFiles: (file: File) => void;
  saveThumb: (file: File) => void;
}


const HeaderLayout: ForwardRefRenderFunction<
  HTMLDivElement,
  HeaderLayoutProps
> = ({ openPreview, downloadFiles, saveThumb }, ref) => {
  const exportButtonRef = useRef<HTMLDivElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  const [openExportSetting, setOpenExportSetting] = useState(false);
  const { actions, query, pages, resizeOptions } = useEditor((state) => ({
    pages: state.pages,
    resizeOptions: state.resizeOptions
  }));

  const [exportId, setExportId] = React.useState('yourId');
  const [templateCategory, setTemplateCategory] = React.useState('basic');
  const [a4, setA4] = useState(false);
  const [usletter, setUSLetter] = useState(false);
  const [a4landscape, setA4Landscape] = useState(false);
  const [usletterlandscape, setUSletterLandscape] = useState(false);
  const [a7card, setA7Card] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [user, setUser] = useState(null);

  const loadTemplate = async () => {
    const APIURL = 'https://vectordad.com/apps/editor/api';
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    AppContext.templateId = id || 'yourid';
    const tc = urlParams.get('tc');
    setTemplateCategory(tc);
    if (!id) return;
    try {
      let response = await fetch(APIURL + '/templates/' + tc + '/' + id + '.json');
      let responseJson = await response.json();
      actions.setData(responseJson);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    setA4(resizeOptions && resizeOptions.a4 ? true : false);
    setUSLetter(resizeOptions && resizeOptions.usletter ? true : false);
    setA4Landscape(resizeOptions && resizeOptions.a4landscape ? true : false);
    setUSletterLandscape(resizeOptions && resizeOptions.usletterlandscape ? true : false);
    setA7Card(resizeOptions && resizeOptions.a7card ? true : false);
    setExportId(AppContext.templateId);
    const checkAuthStatus = async () => {
      AppContext.isAdmin = false;
      try {
        const response = await axios.get('https://vectordad.com/wp-json/custom/v1/check-auth', { withCredentials: true });
        console.log(response.data.data);
        if (response.data.data.wp_logged_in_cookie == "not set") {
          AppContext.isAdmin = false;
        } else {
          AppContext.isAdmin = true;
        }
      } catch (error) {
        AppContext.isAdmin = false;
      }
    };
    checkAuthStatus();
  }, [AppContext.templateId]);

  const handleExport = () => {
    let json = query.serialize();
    if (!a4) {
      delete json[0].resizeOptions.a4;
    }
    if (!a4landscape) {
      delete json[0].resizeOptions.a4landscape;
    }
    if (!usletter) {
      delete json[0].resizeOptions.usletter;
    }
    if (!usletterlandscape) {
      delete json[0].resizeOptions.usletterlandscape;
    }
    if (!a7card) {
      delete json[0].resizeOptions.a7card;
    }
    downloadObjectAsJson(AppContext.templateId, json);
  };

  const handleDownload = (type) => {
    downloadFiles({ type: type, name: AppContext.downloadFileName });
  };

  const handleImport = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    AppContext.templateId = file.name.split('.')[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function () {
        const fileContent: SerializedPage[] = JSON.parse(reader.result as string);
        actions.setData(fileContent);
      };
      reader.readAsText(file);
      e.target.value = '';
    }
  };

  const handleUndo = () => {
    actions.history.undo();
  }

  const handleRedo = () => {
    actions.history.redo();
  }

  const handleSave = async () => {
    let json = query.serialize();
    if (!a4) {
      delete json[0].resizeOptions.a4;
    }
    if (!a4landscape) {
      delete json[0].resizeOptions.a4landscape;
    }
    if (!usletter) {
      delete json[0].resizeOptions.usletter;
    }
    if (!usletterlandscape) {
      delete json[0].resizeOptions.usletterlandscape;
    }
    if (!a7card) {
      delete json[0].resizeOptions.a7card;
    }
    const UPLOAD_URL = "https://vectordad.com/apps/editor/api/";
    const formData = new FormData();
    const jsonData = JSON.stringify(json);
    formData.append('jsonData', jsonData);
    formData.append('fileName', exportId);
    formData.append('folderName', templateCategory);
    const jsonupload = await axios.post(UPLOAD_URL + "uploadtemplate.php", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    saveThumb({ name: exportId });
    console.log(jsonupload.data);
  }

  return (
    <div
      ref={ref}
      css={{
        background: '#f5f5f5',
        padding: '12px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '@media (max-width: 900px)': {
          padding: 12,
        },
      }}
    >
      <div
        css={{
          color: '#3d8eff',
          fontSize: 36,
        }}
      >
        <div
          css={{ color: 'white', height: 42, paddingTop: 6, paddingBottom: 6 }}
        >
          <a href="https://vectordad.com" rel="noreferrer" target="_blank">
            <img css={{ maxHeight: '100%' }} src={'https://vectordad.com/wp-content/uploads/2021/12/VectorDad-1.png'} onLoad={loadTemplate} />
          </a>
        </div>
      </div>
      <div css={{ display: 'flex', alignItems: 'center' }}>
        <div
          css={{
            margin: '0 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#000',
            width: 36,
            height: 36,
            borderRadius: '50%',
            cursor: 'pointer',
            ':hover': {
              background: 'rgba(58,58,76,0.1)',
            },
          }}
          onClick={handleUndo}
        >
          <UndoIcon />
        </div>
        <div
          css={{
            margin: '0 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#000',
            width: 36,
            height: 36,
            borderRadius: '50%',
            cursor: 'pointer',
            ':hover': {
              background: 'rgba(58,58,76,0.1)',
            },
          }}
          onClick={handleRedo}
        >
          <RedoIcon />
        </div>
        {AppContext.isAdmin &&
          <>
            <div
              css={{
                margin: '0 16px',
                cursor: 'pointer',
                color: '#000',
                fontWeight: 700,
                ':hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={() => uploadRef.current?.click()}
            >
              <input
                ref={uploadRef}
                accept="application/json"
                css={{ display: 'none' }}
                type="file"
                onChange={handleImport}
              />
              Import
            </div>
            <Fragment>
              <div
                css={{
                  height: 24,
                  width: `1px`,
                  background: 'rgba(57,76,96,.15)',
                }}
              />
              <div ref={exportButtonRef}
                css={{
                  margin: '0 16px',
                  cursor: 'pointer',
                  color: '#000',
                  fontWeight: 700,
                  ':hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={() => setOpenExportSetting(true)}
              >
                Export
              </div>
              <Popover
                anchorEl={exportButtonRef.current}
                offsets={{
                  'bottom-end': { x: 130, y: 20 },
                }}
                open={openExportSetting}
                placement={'bottom-end'}
                onClose={() => setOpenExportSetting(false)}
              >
                <div css={{
                  display: 'grid',
                  width: 'max-content',
                  padding: 15,
                  gap: 5
                }}>
                  <input css={{
                    height: 28,
                    padding: 4,
                    border: '1px solid #d9d9d9',
                    borderRadius: 4
                  }}
                    value={exportId}
                    onChange={(e) => setExportId(e.target.value)}
                    type="text" />
                  <label>
                    <input
                      type="checkbox"
                      checked={a4}
                      onChange={(e) => { setA4(e.target.checked) }}
                    /> A4</label>
                  <label>
                    <input
                      type="checkbox"
                      checked={usletter}
                      onChange={(e) => { setUSLetter(e.target.checked) }}
                    /> US Letter</label>
                  <label>
                    <input
                      type="checkbox"
                      checked={a4landscape}
                      onChange={(e) => { setA4Landscape(e.target.checked) }}
                    /> A4 Landscape</label>
                  <label>
                    <input
                      type="checkbox"
                      checked={usletterlandscape}
                      onChange={(e) => { setUSletterLandscape(e.target.checked) }}
                    /> US Letter Landscape</label>
                  <label>
                    <input
                      type="checkbox"
                      checked={a7card}
                      onChange={(e) => { setA7Card(e.target.checked) }}
                    /> A7 Card</label>
                </div>
                <div css={{
                  display: 'flex'
                }}>
                  <SettingButton
                    css={{
                      background: '#777',
                      borderRadius: 0,
                      color: '#fff',
                      width: '50%',
                      ':hover': {
                        background: '#888',
                      }
                    }}
                    onClick={() => { handleSave() }}
                  >
                    Save
                  </SettingButton>
                  <SettingButton
                    css={{
                      background: '#666',
                      borderRadius: 0,
                      width: '50%',
                      color: '#fff',
                      ':hover': {
                        background: '#777',
                      }
                    }}
                    onClick={() => { handleExport() }}
                  >
                    Download
                  </SettingButton>
                </div>
              </Popover>
            </Fragment>
          </>
        }
        <div>
          <input
            css={{
              height: 28,
              padding: 4,
              border: '1px solid #d9d9d9',
              borderRadius: 4
            }}
            placeholder="template name"
            onChange={(e) => AppContext.downloadFileName = e.target.value}
            type="text" />
        </div>
        <Menu
          menuButton={<MenuButton
            css={{
              margin: '0 10px',
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              lineHeight: 1,
              background: 'rgb(62,154,46)',
              padding: '12px 16px',
              borderRadius: 8,
              cursor: 'pointer',
              ':hover': {
                background: 'rgb(56,150,40)',
              },
            }}
          >Download</MenuButton>}>
          <MenuItem css={{ padding: 2 }}><a href="https://www.buymeacoffee.com/vectordad" target="_blank"><img width="160" src="https://vectordad.com/wp-content/uploads/2024/08/buymecoffee.png"/></a></MenuItem>
          <MenuItem css={{ padding: '10px 15px' }} onClick={() => handleDownload('png')}><ImageIcon css={{ zoom: 1.5, marginRight: '4px' }} /> PNG</MenuItem>
          <MenuItem css={{ padding: '10px 15px' }} onClick={() => handleDownload('jpg')}><ImageIcon css={{ zoom: 1.5, marginRight: '4px' }} /> JPG</MenuItem>
          <MenuItem css={{ padding: '10px 15px' }} onClick={() => handleDownload('svg')}><ImageIcon css={{ zoom: 1.5, marginRight: '4px' }} /> SVG</MenuItem>
          <MenuItem css={{ padding: '10px 15px' }} onClick={() => handleDownload('pdf')}><DocumentIcon css={{ zoom: 1.5, marginRight: '4px' }} /> PDF</MenuItem>
          {AppContext.isAdmin && (
            <MenuItem css={{ padding: 10 }} onClick={() => handleDownload('thumb')}><ImageIcon css={{ zoom: 1.5, marginRight: '4px' }} /> Thumbnail</MenuItem>
          )}
        </Menu>
      </div>
    </div>
  );
};

export default forwardRef(HeaderLayout);
